<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useSession } from '@/auth';
import { trpc } from '@/trpc';
import { Plus, Layers, Library, GraduationCap } from 'lucide-vue-next';

interface Curriculum {
  id: string;
  title: string;
  state: string;
  reviewsDue: number;
  lessonsRemaining: number;
}

const router = useRouter();
const route = useRoute();
const session = useSession();
const user = computed(() => session.value?.data?.user);

const curricula = ref<Curriculum[]>([]);

const activeCurriculumId = computed(() => {
  const id = route.params.id;
  return typeof id === 'string' ? id : null;
});

// Total due across every curriculum — the badge on the global Review link. Summed from
// the same data that feeds the list, so no extra request.
const totalDue = computed(() =>
  curricula.value.reduce((sum, c) => sum + c.reviewsDue, 0)
);

const reviewActive = computed(() => route.name === 'review' && !activeCurriculumId.value);

const decksActive = computed(() => route.path.startsWith('/decks'));

// Active on the curricula landing only; individual curricula highlight in the list below.
const curriculaActive = computed(() => route.name === 'learn');

async function fetchCurricula() {
  if (!user.value) return;
  try {
    curricula.value = await trpc.listCurriculaWithProgress.query();
  } catch (e) {
    console.error('Failed to load curricula for sidebar', e);
  }
}

onMounted(fetchCurricula);
watch(user, (u) => {
  if (u) fetchCurricula();
});
// Refetch when navigating between curricula so review counts stay fresh.
watch(() => route.fullPath, fetchCurricula);
</script>

<template>
  <div class="px-2 pt-2 pb-1">
    <button
      type="button"
      class="flex items-center gap-2 w-full rounded-md px-2 py-1.5 text-sm transition-colors"
      :class="
        reviewActive
          ? 'bg-accent text-foreground font-medium'
          : 'text-muted-foreground hover:bg-accent/50 hover:text-foreground'
      "
      @click="router.push('/review')"
    >
      <Layers class="size-4 shrink-0" />
      <span class="flex-1 text-left">Review</span>
      <span
        v-if="totalDue > 0"
        class="shrink-0 text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-primary text-primary-foreground"
      >
        {{ totalDue }}
      </span>
    </button>

    <button
      type="button"
      class="flex items-center gap-2 w-full rounded-md px-2 py-1.5 text-sm transition-colors"
      :class="
        curriculaActive
          ? 'bg-accent text-foreground font-medium'
          : 'text-muted-foreground hover:bg-accent/50 hover:text-foreground'
      "
      @click="router.push('/learn')"
    >
      <GraduationCap class="size-4 shrink-0" />
      <span class="flex-1 text-left">Curricula</span>
    </button>

    <button
      type="button"
      class="flex items-center gap-2 w-full rounded-md px-2 py-1.5 text-sm transition-colors"
      :class="
        decksActive
          ? 'bg-accent text-foreground font-medium'
          : 'text-muted-foreground hover:bg-accent/50 hover:text-foreground'
      "
      @click="router.push('/decks')"
    >
      <Library class="size-4 shrink-0" />
      <span class="flex-1 text-left">Decks</span>
    </button>
  </div>

  <div class="px-2 pb-2 space-y-0.5">
    <button
      class="flex items-center gap-2 w-full rounded-md px-2 py-1.5 text-sm text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
      @click="router.push('/cards/new')"
    >
      <Plus class="size-4" />
      <span>New card</span>
    </button>
    <button
      class="flex items-center gap-2 w-full rounded-md px-2 py-1.5 text-sm text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
      @click="router.push('/learn/new')"
    >
      <Plus class="size-4" />
      <span>New curriculum</span>
    </button>
  </div>

  <div class="px-2 pb-2">
    <div class="px-2 py-1 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
      Curricula
    </div>
    <div
      v-if="curricula.length === 0"
      class="px-2 py-2 text-xs text-muted-foreground"
    >
      No curricula yet.
    </div>
    <div class="space-y-0.5">
      <button
        v-for="c in curricula"
        :key="c.id"
        type="button"
        class="flex items-center gap-2 w-full text-left rounded-md px-2 py-1.5 text-sm transition-colors"
        :class="
          c.id === activeCurriculumId
            ? 'bg-accent text-foreground font-medium'
            : 'text-muted-foreground hover:bg-accent/50 hover:text-foreground'
        "
        @click="router.push(`/learn/${c.id}`)"
      >
        <span class="flex-1 truncate">{{ c.title }}</span>
        <span
          v-if="c.reviewsDue > 0"
          class="shrink-0 text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-secondary text-secondary-foreground"
        >
          {{ c.reviewsDue }}
        </span>
      </button>
    </div>
  </div>
</template>
